import { useState } from "react";
import { X, Plus, Edit, Trash2, Save, BookOpen } from "lucide-react";
import { api } from "../../lib/api";

// ---------------- Resource CMS Manager ----------------

const emptyResource = {
  slug: "",
  title: "",
  category: "Veteran Disability",
  type: "Article",
  excerpt: "",
  content: "",
  externalUrl: "",
  published: true,
};

const CATEGORY_OPTS = ["Veteran Disability", "Insurance", "Education"];
const TYPE_OPTS = ["Article", "FAQ", "Video", "Download"];

const ResourceManager = ({ resources, refresh }) => {
  const [editing, setEditing] = useState(null); // null | 'new' | resource object
  const [form, setForm] = useState(emptyResource);
  const [saving, setSaving] = useState(false);

  const startNew = () => {
    setEditing("new");
    setForm(emptyResource);
  };

  const startEdit = (r) => {
    setEditing(r);
    setForm({
      slug: r.slug,
      title: r.title,
      category: r.category,
      type: r.type,
      excerpt: r.excerpt,
      content: r.content || "",
      externalUrl: r.externalUrl || "",
      published: r.published,
    });
  };

  const cancel = () => {
    setEditing(null);
    setForm(emptyResource);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, externalUrl: form.externalUrl || null };
      if (editing === "new") {
        await api.post(`/resources`, payload);
      } else {
        await api.put(`/resources/${editing.id}`, payload);
      }
      await refresh();
      cancel();
    } catch (err) {
      console.error("Save error:", err);
      alert("Failed to save. Check console for details.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete resource "${r.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/resources/${r.id}`);
      await refresh();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete.");
    }
  };

  if (editing) {
    return (
      <div className="bg-white p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-xl font-bold text-slate-900">
            {editing === "new" ? "Create Resource" : "Edit Resource"}
          </h3>
          <button onClick={cancel} className="text-slate-500 hover:text-slate-900">
            <X size={22} />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ResField label="Title *">
              <input
                name="title"
                required
                value={form.title}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:border-slate-900"
                data-testid="res-title"
              />
            </ResField>
            <ResField label="Slug (URL)">
              <input
                name="slug"
                value={form.slug}
                onChange={handleChange}
                placeholder="auto-generated from title if blank"
                className="w-full bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:border-slate-900"
                data-testid="res-slug"
              />
            </ResField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ResField label="Category *">
              <select
                name="category"
                value={form.category}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 px-4 py-3 outline-none"
                data-testid="res-category"
              >
                {CATEGORY_OPTS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </ResField>
            <ResField label="Type *">
              <select
                name="type"
                value={form.type}
                onChange={handleChange}
                className="w-full bg-slate-50 border border-slate-200 px-4 py-3 outline-none"
                data-testid="res-type"
              >
                {TYPE_OPTS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </ResField>
          </div>

          <ResField label="Excerpt *">
            <textarea
              name="excerpt"
              required
              rows={2}
              value={form.excerpt}
              onChange={handleChange}
              className="w-full bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:border-slate-900 resize-none"
              placeholder="Short description shown on the resources list"
              data-testid="res-excerpt"
            />
          </ResField>

          <ResField label="Content (Full article body)">
            <textarea
              name="content"
              rows={10}
              value={form.content}
              onChange={handleChange}
              className="w-full bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:border-slate-900 resize-y font-mono text-sm"
              placeholder="Body text — will preserve line breaks"
              data-testid="res-content"
            />
          </ResField>

          <ResField label="External URL (optional)">
            <input
              name="externalUrl"
              value={form.externalUrl}
              onChange={handleChange}
              placeholder="e.g., link to a video or PDF"
              className="w-full bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:border-slate-900"
              data-testid="res-external"
            />
          </ResField>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="published"
              checked={form.published}
              onChange={handleChange}
              className="w-4 h-4"
              data-testid="res-published"
            />
            <span className="text-sm text-slate-700">Published (visible on public site)</span>
          </label>

          <div className="flex gap-3 pt-4 border-t">
            <button
              onClick={save}
              disabled={saving || !form.title || !form.excerpt}
              className="inline-flex items-center gap-2 bg-[#0B2343] text-white px-6 py-3 font-medium hover:bg-slate-800 disabled:opacity-50"
              data-testid="res-save"
            >
              <Save size={16} />
              {saving ? "Saving..." : "Save Resource"}
            </button>
            <button
              onClick={cancel}
              className="px-6 py-3 border border-slate-300 text-slate-700 font-medium hover:bg-slate-50"
              data-testid="res-cancel"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <div className="p-4 border-b border-slate-100 flex justify-between items-center">
        <h3 className="font-bold text-slate-900">All Resources ({resources.length})</h3>
        <button
          onClick={startNew}
          className="inline-flex items-center gap-2 bg-[#0B2343] text-white px-4 py-2 text-sm font-medium hover:bg-slate-800"
          data-testid="res-new"
        >
          <Plus size={16} /> New Resource
        </button>
      </div>

      {resources.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="mx-auto text-slate-300 mb-4" size={48} />
          <p className="text-slate-500">No resources yet. Create your first one.</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {resources.map((r) => (
            <div key={r.id} className="p-4 flex items-start justify-between gap-4" data-testid={`res-row-${r.slug}`}>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h4 className="font-bold text-slate-900">{r.title}</h4>
                  {!r.published && (
                    <span className="text-xs bg-slate-200 text-slate-700 px-2 py-0.5">Draft</span>
                  )}
                </div>
                <p className="text-slate-500 text-sm">
                  {r.category} · {r.type} · /{r.slug}
                </p>
                <p className="text-slate-600 text-sm mt-1 line-clamp-2">{r.excerpt}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => startEdit(r)}
                  className="p-2 text-slate-600 hover:bg-slate-100"
                  data-testid={`res-edit-${r.slug}`}
                  title="Edit"
                >
                  <Edit size={18} />
                </button>
                <button
                  onClick={() => remove(r)}
                  className="p-2 text-red-600 hover:bg-red-50"
                  data-testid={`res-delete-${r.slug}`}
                  title="Delete"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ResField = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
    {children}
  </div>
);

export default ResourceManager;
